// @ts-nocheck
import React, { useState, useEffect } from 'react';
import 'react-dropzone-uploader/dist/styles.css';
import Dropzone, { IFileWithMeta } from 'react-dropzone-uploader';
import css from './css';

export interface FileUploaderProps {
  onUploadParams: (metadata, error) => Promise<any>;
  startUpload?: boolean,
  onUploadBegin?: () => void,
  onComplete: (file: IFileWithMeta) => void,
  disabled?: boolean,
  maxFiles: number,
  acceptedFileTypes?: string,
  acceptedFilesName?: string,
  autoUpload?: boolean
  buttonOnly?: boolean
  canRestart?: boolean
  canCancel?: boolean
}

export const FileUploader: React.FC<FileUploaderProps> = ({
  onUploadParams,
  canRestart,
  canCancel,
  startUpload = false,
  onUploadBegin = () => null,
  onComplete,
  disabled,
  maxFiles = 50,
  acceptedFileTypes = '*',
  acceptedFilesName = 'Any',
  autoUpload,
  buttonOnly,
}) => {
  const [filesReadyToUpload, setFilesReadyToUpload] = useState([]);
  const [errorMessage, setErrorMessage] = useState('');

  const handleChangeStatus = (fileMetadata: IFileWithMeta, status) => {
    setErrorMessage('');
    switch (status) {
      case 'uploading':
        onUploadBegin();
        break;
      case 'rejected_file_type':
        setErrorMessage(`Invalid file type. Please select a ${acceptedFilesName} file`);
        break;
      case 'ready':
        setFilesReadyToUpload([...filesReadyToUpload, fileMetadata]);
        break;
      case 'done':
        onComplete(fileMetadata);
        break;
      case 'exception_upload':
      case 'error_upload':
      case 'error_upload_params':
        setErrorMessage('Failed to upload your file(s). Please try again.');
        break;
      default:
        break;
    }
  };

  useEffect(() => {
    if (startUpload) {
      filesReadyToUpload.forEach((fileMetadata) => {
        fileMetadata.restart();
      });
    }
  }, [startUpload]);

  const renderInputContent = (files, extra) => (
    <div key="advanced-uploader">
      {extra.reject
        && <p>{`${acceptedFilesName} files only`}</p>}
      <div className={styles.inputContent}>
        <p className={styles.uploadText}>Drag & drop files or</p>
        <div className={styles.uploadButton}>Click to upload</div>
      </div>
    </div>
  );

  const renderBasicInputContent = (files, extra) => (
    <div key="basic-uploader">
      {extra.reject
        && <p>{`${acceptedFilesName} files only`}</p>}
      <div className={styles.inputContent}>
        <div className={styles.uploadButton}>Add Files</div>
      </div>
    </div>
  );

  return (
    <div data-testid="shared-file-uploader">
      <Dropzone
        canRestart={!!canRestart}
        canCancel={!!canCancel}
        getUploadParams={onUploadParams}
        onChangeStatus={handleChangeStatus}
        accept={acceptedFileTypes}
        autoUpload={autoUpload}
        submitButtonContent={null}
        multiple={false}
        disabled={disabled}
        maxFiles={maxFiles}
        inputContent={(files, extra) => (buttonOnly
          ? renderBasicInputContent(files, extra) : renderInputContent(files, extra)
        )}
        styles={{
          inputLabel: (files, extra) => (extra.reject ? { color: '#EB1300' } : {}),
          dropzone: {
            minHeight: buttonOnly ? '100px' : '224px',
            border: 'dashed',
            borderWidth: '1px',
            borderColor: '#D4D4D4',
            backgroundColor: '#F7F7F7',
            overflow: 'auto',
          },
          dropzoneActive: { borderColor: '#4BAEF8', background: 'rgba(75, 174, 248, 0.05)' },
          dropzoneReject: { borderColor: '#EB1300', background: 'rgba(235, 19, 0, 0.05)' },
        }}
      />
      { errorMessage && <div style={{ color: '#EB1300' }}>{errorMessage}</div> }
    </div>
  );
};

const styles = css({
  inputContent: {
    display: 'flex',
    flexDirection: 'column',
    color: '#41403B',
    padding: '20px',
  },

  uploadText: {
    marginBottom: '14px',
    fontSize: '18px',
    fontWeight: 900,
  },

  // mock button mirroring shared button component styles
  uploadButton: {
    textAlign: 'center',
    verticalAlign: 'middle',
    display: 'inline-block',
    cursor: 'pointer',
    whiteSpace: 'nowrap',
    textOverflow: 'ellipsis',
    textDecoration: 'none',
    WebkitAppearance: 'none',
    fontWeight: 900,
    fontFamily: 'Lato, sans-serif',
    userSelect: 'none',
    border: 'none',
    borderRadius: '5px',
    marginRight: 4,
    lineHeight: 1.5,
    marginTop: '.25rem',
    marginBottom: '.25rem',
    textTransform: 'uppercase',
    color: '#fff',
    fill: '#fff',
    backgroundColor: '#4baef8',
    padding: '6.6px 14.4px',
    fontSize: '11px',

    ':hover': {
      color: '#fff',
    },

    ':focus': {
      outline: 0,
    },
  },
});

export default FileUploader;
